import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  makeTempId,
  buildItemLocalizedDetails,
  getPrimaryMenuLanguage,
  getMenuOptionalLanguages,
  getChoiceDisplayTitle,
  getItemDisplayTitle,
} from "./utils";
import { showWizardToast, showWizardValidation } from "../wizardFeedback";
import {
  formatMoney,
  getCurrencySymbol,
  getRestaurantCurrencyCode,
} from "../../../../../utils/currency";

function countChoicesForItem(itemId, choiceItems) {
  let count = 0;
  Object.values(choiceItems || {}).forEach((ids) => {
    if (Array.isArray(ids) && ids.includes(itemId)) count += 1;
  });
  return count;
}

function getChoiceIdsForItem(itemId, choiceItems) {
  return Object.entries(choiceItems || {})
    .filter(([, ids]) => Array.isArray(ids) && ids.includes(itemId))
    .map(([choiceId]) => choiceId);
}

function syncItemChoiceAssignments(choiceItems, itemId, selectedChoiceIds) {
  const next = { ...(choiceItems || {}) };
  const selected = new Set((selectedChoiceIds || []).filter(Boolean));

  Object.keys(next).forEach((choiceId) => {
    const current = Array.isArray(next[choiceId]) ? next[choiceId] : [];
    if (selected.has(choiceId)) {
      if (!current.includes(itemId)) next[choiceId] = [...current, itemId];
      return;
    }
    const filtered = current.filter((id) => id !== itemId);
    if (filtered.length) next[choiceId] = filtered;
    else delete next[choiceId];
  });

  selected.forEach((choiceId) => {
    if (!next[choiceId]) next[choiceId] = [itemId];
  });

  return next;
}

const EMPTY_REL = Object.freeze({});

const Step4Items = ({
  draft,
  setDraft,
  goNext,
  goPrev,
  setLiveDetailCandidate,
}) => {
  const { t } = useTranslation();
  const menuPrimaryLanguage = getPrimaryMenuLanguage(draft?.menu);
  const menuOptionalLanguages = getMenuOptionalLanguages(draft?.menu);
  const choices = Array.isArray(draft?.choices) ? draft.choices : [];
  const items = Array.isArray(draft?.items) ? draft.items : [];
  const choiceItems = draft?.relations?.choiceItems || EMPTY_REL;
  const restaurantCurrency = getRestaurantCurrencyCode();
  const currencySymbol = getCurrencySymbol(restaurantCurrency);

  const [title, setTitle] = useState("");
  const [price, setPrice] = useState("");
  const [havePrice, setHavePrice] = useState(true);
  const [editingItemId, setEditingItemId] = useState(null);
  const [selectedItemIds, setSelectedItemIds] = useState(() => new Set());
  const [itemsQuery, setItemsQuery] = useState("");
  const [assignChoiceIds, setAssignChoiceIds] = useState(() => new Set());
  const [assignChoiceQuery, setAssignChoiceQuery] = useState("");
  const [assignDropdownOpen, setAssignDropdownOpen] = useState(false);
  const itemFormRef = useRef(null);
  const assignDropdownRef = useRef(null);

  const choiceOptions = useMemo(
    () =>
      choices.map((choice) => ({
        id: choice.tempId,
        title: getChoiceDisplayTitle(choice, menuPrimaryLanguage),
      })),
    [choices, menuPrimaryLanguage]
  );
  const selectedAssignChoices = useMemo(
    () => choiceOptions.filter((choice) => assignChoiceIds.has(choice.id)),
    [choiceOptions, assignChoiceIds]
  );
  const filteredChoiceOptions = useMemo(() => {
    const query = assignChoiceQuery.trim().toLowerCase();
    if (!query) return choiceOptions;
    return choiceOptions.filter((choice) =>
      String(choice.title || "")
        .toLowerCase()
        .includes(query)
    );
  }, [choiceOptions, assignChoiceQuery]);

  const filteredItems = useMemo(() => {
    const query = itemsQuery.trim().toLowerCase();
    if (!query) return items;
    return items.filter((item) =>
      String(getItemDisplayTitle(item, menuPrimaryLanguage) || "")
        .toLowerCase()
        .includes(query)
    );
  }, [items, itemsQuery, menuPrimaryLanguage]);

  const livePreview = useMemo(() => {
    const trimmed = String(title || "").trim();
    return {
      editingItemId: editingItemId || null,
      assignChoiceIds: [...assignChoiceIds],
      formItem: {
        id: editingItemId || "preview-item",
        title: trimmed,
        price: havePrice ? Number(price || 0) : 0,
        havePrice,
      },
    };
  }, [title, price, havePrice, editingItemId, assignChoiceIds]);

  // Items step: parent opens the untitled product-detail preview first.
  // Back on the phone returns to the menu list so a real product can be opened.
  useLayoutEffect(() => {
    setLiveDetailCandidate?.(livePreview);
    return undefined;
  }, [livePreview, setLiveDetailCandidate]);

  useEffect(() => {
    if (!assignDropdownOpen) return undefined;
    const onPointerDown = (event) => {
      if (!assignDropdownRef.current?.contains(event.target)) {
        setAssignDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [assignDropdownOpen]);

  const toggleItemSelection = (itemId) => {
    setSelectedItemIds((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  const getAssignableItemIds = (choiceId, sourceIds) => {
    const assignedOnChoice = new Set(choiceItems[choiceId] || []);
    return [...new Set((sourceIds || []).filter(Boolean))].filter(
      (itemId) => !assignedOnChoice.has(itemId)
    );
  };

  const assignItemsToChoice = (choiceId, itemIds) => {
    const uniqueIds = getAssignableItemIds(choiceId, itemIds);
    if (!uniqueIds.length || !choiceId) return;

    setDraft((prev) => {
      const rel = prev.relations || {};
      const nextChoiceItems = { ...(rel.choiceItems || {}) };
      const current = new Set(nextChoiceItems[choiceId] || []);
      uniqueIds.forEach((id) => current.add(id));
      nextChoiceItems[choiceId] = [...current];
      return {
        ...prev,
        relations: { ...rel, choiceItems: nextChoiceItems },
      };
    });
    showWizardToast(t, "wizardItemsAssigned", { count: uniqueIds.length });
    setSelectedItemIds(new Set());
  };

  const unassignItemFromChoice = (choiceId, itemId) => {
    setDraft((prev) => {
      const rel = prev.relations || {};
      const nextChoiceItems = { ...(rel.choiceItems || {}) };
      const filtered = (nextChoiceItems[choiceId] || []).filter((id) => id !== itemId);
      if (filtered.length) nextChoiceItems[choiceId] = filtered;
      else delete nextChoiceItems[choiceId];
      return {
        ...prev,
        relations: { ...rel, choiceItems: nextChoiceItems },
      };
    });
  };

  const canSave = Boolean(title.trim());

  const resetItemForm = () => {
    setTitle("");
    setPrice("");
    setHavePrice(true);
    setEditingItemId(null);
    setAssignChoiceIds(new Set());
    setAssignChoiceQuery("");
    setAssignDropdownOpen(false);
  };

  const toggleAssignChoice = (choiceId) => {
    if (!choiceId) return;
    setAssignChoiceIds((prev) => {
      const next = new Set(prev);
      if (next.has(choiceId)) next.delete(choiceId);
      else next.add(choiceId);
      return next;
    });
  };

  useEffect(() => {
    if (!editingItemId || !itemFormRef.current) return;
    itemFormRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    document.getElementById("wizard-item-title")?.focus({ preventScroll: true });
  }, [editingItemId]);

  const startEditItem = (item) => {
    setEditingItemId(item.tempId);
    setTitle(getItemDisplayTitle(item, menuPrimaryLanguage) || "");
    setHavePrice(item.havePrice !== false);
    setPrice(item.havePrice !== false ? String(item.price ?? 0) : "");
    setAssignChoiceIds(new Set(getChoiceIdsForItem(item.tempId, choiceItems)));
  };

  const saveItem = () => {
    if (!title.trim()) {
      showWizardValidation(t, "wizardItemTitleRequired");
      return;
    }

    const itemDetails = buildItemLocalizedDetails({
      primaryLanguage: menuPrimaryLanguage,
      optionalLanguages: menuOptionalLanguages,
      titles: { [menuPrimaryLanguage]: title },
    });

    const itemPayload = {
      havePrice,
      price: havePrice ? Number(price || 0) : 0,
      itemDetails,
    };
    const selectedAssignIds = [...assignChoiceIds];

    if (editingItemId) {
      setDraft((prev) => {
        const rel = prev.relations || {};
        return {
          ...prev,
          items: (prev.items || []).map((item) =>
            item.tempId === editingItemId ? { ...item, ...itemPayload } : item
          ),
          relations: {
            ...rel,
            choiceItems: syncItemChoiceAssignments(
              rel.choiceItems,
              editingItemId,
              selectedAssignIds
            ),
          },
        };
      });
      showWizardToast(
        t,
        selectedAssignIds.length ? "wizardItemUpdatedAndAssigned" : "wizardItemUpdated",
        selectedAssignIds.length ? { count: selectedAssignIds.length } : undefined
      );
    } else {
      const newItemId = makeTempId("item");
      setDraft((prev) => {
        const rel = prev.relations || {};
        const nextChoiceItems = selectedAssignIds.length
          ? syncItemChoiceAssignments(rel.choiceItems, newItemId, selectedAssignIds)
          : rel.choiceItems || {};
        return {
          ...prev,
          items: [
            ...(Array.isArray(prev.items) ? prev.items : []),
            {
              tempId: newItemId,
              ...itemPayload,
            },
          ],
          relations: selectedAssignIds.length
            ? { ...rel, choiceItems: nextChoiceItems }
            : prev.relations,
        };
      });
      showWizardToast(
        t,
        selectedAssignIds.length ? "wizardItemAddedAndAssigned" : "wizardItemAdded",
        selectedAssignIds.length ? { count: selectedAssignIds.length } : undefined
      );
    }

    resetItemForm();
  };

  const removeItem = (itemTempId) => {
    if (editingItemId === itemTempId) {
      resetItemForm();
    }
    setDraft((prev) => {
      const next = { ...prev };
      next.items = (prev.items || []).filter((it) => it.tempId !== itemTempId);

      const nextRelations = { ...(prev.relations || {}) };
      nextRelations.choiceItems = { ...(nextRelations.choiceItems || {}) };
      for (const [choiceTempId, itemTempIds] of Object.entries(nextRelations.choiceItems)) {
        if (!Array.isArray(itemTempIds)) continue;
        const filtered = itemTempIds.filter((id) => id !== itemTempId);
        if (filtered.length) nextRelations.choiceItems[choiceTempId] = filtered;
        else delete nextRelations.choiceItems[choiceTempId];
      }

      next.relations = nextRelations;
      return next;
    });
    showWizardToast(t, "wizardItemDeleted");
  };

  return (
    <>
      <div className="mt-3 start-step-content">
        <div className="start-step-hero">
          <h2 className="start-step-title">
            <span className="step-badge">6</span>
            {t("wizardItemsHeading")}
          </h2>
          <p className="start-step-subtitle">{t("wizardItemsSub")}</p>
        </div>

        <div className="start-options">
          <section
            className="start-products-form start-step-hero-scroll-target"
            ref={itemFormRef}
            aria-label={editingItemId ? t("wizardEditItemLabel") : t("wizardAddItemLabel")}
          >
            <div className="start-options-section-head">
              <span className="start-options-section-head__index">1</span>
              <h3 className="start-options-section-head__title">
                {editingItemId ? t("wizardEditItemLabel") : t("wizardAddItemLabel")}
              </h3>
            </div>
            <div className="start-item-fields">
              <div>
                <label className="start-step1-label" htmlFor="wizard-item-title">
                  {t("wizardItemTitleLabel")}
                  <span className="start-step1-required" aria-hidden="true">*</span>
                </label>
                <input
                  id="wizard-item-title"
                  className="form-control start-input"
                  placeholder={t("wizardItemTitlePlaceholder")}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>
              <div>
                <label className="start-step1-label" htmlFor="wizard-item-price">
                  {t("price")}
                  <span className="start-step1-label__lang">{restaurantCurrency}</span>
                </label>
                <div className={`input-group start-price-input-group${!havePrice ? " is-disabled" : ""}`}>
                  <span className="input-group-text start-price-input-prefix">{currencySymbol}</span>
                  <input
                    id="wizard-item-price"
                    type="number"
                    className="form-control start-input start-price-input"
                    value={price}
                    disabled={!havePrice}
                    onChange={(e) => setPrice(e.target.value)}
                  />
                </div>
                <label
                  className={`start-item-price-toggle${!havePrice ? " is-on" : ""}`}
                  htmlFor="wizard-item-no-price"
                >
                  <span>{t("wizardItemNoExtraPrice")}</span>
                  <span className="form-check form-switch m-0">
                    <input
                      id="wizard-item-no-price"
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      checked={!havePrice}
                      onChange={(e) => {
                        const noExtraPrice = e.target.checked;
                        setHavePrice(!noExtraPrice);
                        if (noExtraPrice) setPrice("");
                      }}
                    />
                  </span>
                </label>
              </div>
            </div>
            {choiceOptions.length > 0 ? (
              <div className="start-choice-assign" ref={assignDropdownRef}>
                <div className="start-choice-assign__head">
                  <label className="start-step1-label" htmlFor="wizard-item-assign-search">
                    {t("wizardItemAssignChoicesLabel")}
                  </label>
                  <span className="start-choice-assign__optional">{t("optional")}</span>
                </div>
                {selectedAssignChoices.length > 0 ? (
                  <div className="start-choice-assign__cards" aria-live="polite">
                    {selectedAssignChoices.map((choice) => (
                      <span key={choice.id} className="start-choice-assign__card">
                        <span className="start-choice-assign__card-name">{choice.title}</span>
                        <button
                          type="button"
                          className="start-choice-assign__card-remove"
                          onClick={() => toggleAssignChoice(choice.id)}
                          aria-label={t("wizardItemAssignRemoveChoice", {
                            choice: choice.title,
                          })}
                          title={t("remove") || "Remove"}
                        >
                          <i className="fa fa-times" aria-hidden="true" />
                        </button>
                      </span>
                    ))}
                  </div>
                ) : null}
                <div className={`start-choice-assign__combobox${assignDropdownOpen ? " is-open" : ""}`}>
                  <div className="start-choice-assign__search">
                    <i className="fa fa-search start-choice-assign__search-icon" aria-hidden="true" />
                    <input
                      id="wizard-item-assign-search"
                      type="search"
                      className="start-choice-assign__search-input"
                      placeholder={t("wizardItemAssignChoicesPlaceholder")}
                      value={assignChoiceQuery}
                      onChange={(e) => {
                        setAssignChoiceQuery(e.target.value);
                        setAssignDropdownOpen(true);
                      }}
                      onFocus={() => setAssignDropdownOpen(true)}
                      autoComplete="off"
                    />
                    <button
                      type="button"
                      className="start-choice-assign__search-toggle"
                      onClick={() => setAssignDropdownOpen((open) => !open)}
                      aria-expanded={assignDropdownOpen}
                      aria-label={t("wizardItemAssignChoicesLabel")}
                    >
                      <i className={`fa fa-chevron-${assignDropdownOpen ? "up" : "down"}`} aria-hidden="true" />
                    </button>
                  </div>
                  {assignDropdownOpen ? (
                    <div className="start-choice-assign__menu" role="listbox" aria-multiselectable="true">
                      {filteredChoiceOptions.length === 0 ? (
                        <p className="start-choice-assign__menu-empty">
                          {t("wizardItemAssignChoicesNoResults")}
                        </p>
                      ) : (
                        filteredChoiceOptions.map((choice) => {
                          const selected = assignChoiceIds.has(choice.id);
                          return (
                            <label
                              key={choice.id}
                              className={`start-choice-assign__option${selected ? " is-selected" : ""}`}
                            >
                              <input
                                type="checkbox"
                                className="start-choice-assign__option-input"
                                checked={selected}
                                onChange={() => toggleAssignChoice(choice.id)}
                              />
                              <span className="start-choice-assign__option-label">{choice.title}</span>
                            </label>
                          );
                        })
                      )}
                    </div>
                  ) : null}
                </div>
              </div>
            ) : (
              <p className="start-choice-assign__empty">{t("wizardItemAssignNoChoices")}</p>
            )}
            <div className="start-products-form__actions">
              <button type="button" className="start-static-btn" onClick={resetItemForm}>
                {t("cancel")}
              </button>
              <button
                type="button"
                className="start-products-add"
                disabled={!canSave}
                onClick={saveItem}
              >
                {editingItemId ? t("wizardUpdateItem") : t("wizardAddItem")}
              </button>
            </div>
          </section>

          <section className="start-products-added" aria-label={t("wizardAllItems")}>
            <div className="start-products-added__head">
              <h3 className="start-products-added__title">{t("wizardAllItems")}</h3>
              <span className="start-products-added__count">{items.length}</span>
            </div>
            <p className="start-options-hint">{t("wizardItemsMultiChoiceHint")}</p>
            {items.length === 0 ? (
              <p className="start-products-empty">{t("wizardNoItemsYet")}</p>
            ) : (
              <div className="start-choice-library">
                <div className="start-choice-library__search">
                  <i className="fa fa-search start-choice-library__search-icon" aria-hidden="true" />
                  <input
                    id="wizard-items-search"
                    type="search"
                    className="start-choice-library__search-input"
                    placeholder={t("wizardItemSearchPlaceholder")}
                    value={itemsQuery}
                    onChange={(e) => setItemsQuery(e.target.value)}
                    autoComplete="off"
                    aria-label={t("wizardItemSearchPlaceholder")}
                  />
                  {itemsQuery ? (
                    <button
                      type="button"
                      className="start-choice-library__search-clear"
                      onClick={() => setItemsQuery("")}
                      aria-label={t("clear") || "Clear"}
                      title={t("clear") || "Clear"}
                    >
                      <i className="fa fa-times" aria-hidden="true" />
                    </button>
                  ) : null}
                </div>
                <div className="start-choice-library__meta">
                  <span>
                    {itemsQuery.trim()
                      ? t("wizardItemSearchResults", {
                          count: filteredItems.length,
                          total: items.length,
                        })
                      : t("wizardItemSearchTotal", { count: items.length })}
                  </span>
                </div>
                {filteredItems.length === 0 ? (
                  <p className="start-choice-library__empty">{t("wizardItemSearchNoResults")}</p>
                ) : (
                  <ul className="start-products-rows start-products-rows--scroll">
                    {filteredItems.map((item) => {
                      const isSelected = selectedItemIds.has(item.tempId);
                      const choiceCount = countChoicesForItem(item.tempId, choiceItems);
                      return (
                        <li
                          key={item.tempId}
                          className={`start-products-row start-option-row${isSelected ? " is-selected" : ""}${
                            editingItemId === item.tempId ? " is-editing" : ""
                          }`}
                        >
                          <button
                            type="button"
                            className="start-option-row__select"
                            onClick={() => toggleItemSelection(item.tempId)}
                          >
                            <span className={`start-option-row__check${isSelected ? " is-on" : ""}`}>
                              {isSelected ? <i className="fa fa-check" aria-hidden="true" /> : null}
                            </span>
                            <span className="start-products-row__main">
                              <span className="start-products-row__title">
                                {getItemDisplayTitle(item, menuPrimaryLanguage)}
                              </span>
                              <span className="start-option-row__meta">
                                {item.havePrice
                                  ? formatMoney(item.price ?? 0)
                                  : t("wizardItemNoExtraPrice")}
                                {choiceCount > 0
                                  ? ` · ${t("wizardItemChoiceCount", { count: choiceCount })}`
                                  : ""}
                              </span>
                            </span>
                          </button>
                          <div className="start-products-row__actions">
                            <button
                              type="button"
                              className="start-products-row__icon-btn is-edit"
                              onClick={() => startEditItem(item)}
                              aria-label={t("edit")}
                              title={t("edit")}
                            >
                              <i className="fa fa-edit" aria-hidden="true" />
                            </button>
                            <button
                              type="button"
                              className="start-products-row__icon-btn is-danger"
                              onClick={() => removeItem(item.tempId)}
                              aria-label={t("remove")}
                              title={t("remove")}
                            >
                              <i className="fa fa-trash-alt" aria-hidden="true" />
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
          </section>

          <section className="start-products-added" aria-label={t("wizardAssignItemsToChoices")}>
            <div className="start-products-added__head">
              <h3 className="start-products-added__title">{t("wizardAssignItemsToChoices")}</h3>
              {selectedItemIds.size > 0 ? (
                <span className="start-products-added__count">{selectedItemIds.size}</span>
              ) : null}
            </div>
            {choices.length === 0 ? (
              <p className="start-products-empty">{t("wizardNoChoicesForItems")}</p>
            ) : (
              <div className="start-assign-list">
                {choices.map((choice) => {
                  const assignedIds = choiceItems[choice.tempId] || [];
                  const hasSelection = selectedItemIds.size > 0;
                  const sourceIds = [...selectedItemIds];
                  const pendingIds = hasSelection
                    ? getAssignableItemIds(choice.tempId, sourceIds)
                    : [];
                  const canAssign = hasSelection && pendingIds.length > 0;

                  return (
                    <article key={choice.tempId} className="start-assign-card">
                      <div className="start-assign-card__head">
                        <div className="start-products-row__main">
                          <span className="start-assign-card__name">
                            {getChoiceDisplayTitle(choice, menuPrimaryLanguage)}
                          </span>
                          <span className="start-option-row__meta">
                            {t("wizardChoiceMinLabel")} {choice.minSelected ?? 0}
                            {" · "}
                            {t("wizardChoiceMaxLabel")} {choice.maxSelected ?? 1}
                          </span>
                        </div>
                        {items.length > 0 ? (
                          <button
                            type="button"
                            className="start-assign-btn"
                            disabled={!canAssign}
                            onClick={() => assignItemsToChoice(choice.tempId, sourceIds)}
                          >
                            {!hasSelection
                              ? t("wizardAssignItemsBatch", { count: 0 })
                              : pendingIds.length > 0
                                ? t("wizardAssignItemsBatch", { count: pendingIds.length })
                                : t("wizardItemsAlreadyOnChoice")}
                          </button>
                        ) : null}
                      </div>
                      <div className="start-assign-chips">
                        {assignedIds.length === 0 ? (
                          <p className="start-assign-empty">{t("wizardNoItemsYet")}</p>
                        ) : (
                          assignedIds.map((itemId) => {
                            const item = items.find((it) => it.tempId === itemId);
                            if (!item) return null;
                            return (
                              <span key={itemId} className="start-assign-chip">
                                <i className="fa fa-utensils" aria-hidden="true" />
                                <span className="start-assign-chip__label">
                                  {getItemDisplayTitle(item, menuPrimaryLanguage)}
                                </span>
                                {item.havePrice ? (
                                  <em>{formatMoney(item.price ?? 0)}</em>
                                ) : null}
                                <button
                                  type="button"
                                  className="start-assign-chip__remove"
                                  onClick={() => unassignItemFromChoice(choice.tempId, itemId)}
                                  aria-label={t("remove")}
                                  title={t("remove")}
                                >
                                  <i className="fa fa-times" aria-hidden="true" />
                                </button>
                              </span>
                            );
                          })
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      </div>

      <div className="toolbar toolbar-bottom start-toolbar">
        <button className="btn btn-secondary sw-btn-prev me-1 start-btn-back" onClick={goPrev} type="button">
          ← {t("back")}
        </button>
        <div className="wizard-step-counter">{t("wizardStep6of9")}</div>
        <div className="d-flex gap-2">
          <button className="btn btn-outline-secondary sw-btn-skip" onClick={goNext} type="button">
            {t("wizardSkipStep")}
          </button>
          <button className="btn btn-primary sw-btn-next start-btn-next" onClick={goNext} type="button">
            {t("wizardContinue")} <span aria-hidden="true">→</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default Step4Items;
